import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { isQueryBMM } from './validateQuery';

/**
 * Converts an API vehicle-brand enum name into readable display text.
 *
 * @param brand - Vehicle-brand enum value from the BFF
 * @returns The brand with underscores replaced by spaces, or `undefined`
 * @example formatVehicleBrand('RENAULT_TRUCKS') // 'RENAULT TRUCKS'
 */
export const formatVehicleBrand = (brand?: string): string | undefined => brand?.replaceAll('_', ' ');

/**
 * Returns the display label for a vehicle, resolving the correct name based on catalog source and query type.
 *
 * - DATAHUB vehicles: uses `dataHubVehicle.name`
 * - IAM vehicles searched by brand/model/motorization (BMM): uses `vehicleBrand` or falls back to `iamVehicle.name`,
 *   then adds `iamVehicle.modelType`
 * - IAM vehicles searched by VIN/VRN: uses `iamVehicle.name + iamVehicle.modelType`
 *
 * @param item - The vehicle detail object from the BFF spec
 * @returns The resolved vehicle label string, or `undefined` if the underlying fields are absent
 *
 * @see ONEPORTAL-4810
 */
export const getVehicleLabel = (item: VehicleDetail): string | undefined => {
  if (item.catalogSource === 'DATAHUB') {
    return item.dataHubVehicle?.name;
  }

  const queryIsBmm = isQueryBMM(item.vehicleKey);

  if (queryIsBmm) {
    return (
      [formatVehicleBrand(item.vehicleBrand) || item.iamVehicle?.name, item.iamVehicle?.modelType]
        .filter(Boolean)
        .join(' ') || undefined
    );
  }

  return [item.iamVehicle?.name, item.iamVehicle?.modelType].filter(Boolean).join(' ') || undefined;
};
